'use client';
import { isNull, useStorageState } from '@brand/utils';
import { AppLoader } from 'components/app-loader';
import { IUserInfo, Profile, authService, profileService } from 'lib';
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
  type JSX,
} from 'react';

const noopPromise = (): Promise<void> => Promise.resolve();

type TPricePolicy = 'regular' | 'employee';
type TProvidedValue = {
  isLoading: boolean;
  pricePolicy: TPricePolicy;
  profile: Profile | null;
  updateProfile: (profile: Partial<Profile>) => Promise<void>;
  invalidateProfile: () => Promise<Profile | null>;
  logout: () => Promise<void>;
};

const Context = createContext<TProvidedValue>({
  isLoading: false,
  profile: null,
  pricePolicy: 'regular',
  updateProfile: noopPromise,
  invalidateProfile: () => Promise.resolve(null),
  logout: noopPromise,
});

type TUserInfoProviderProps = {
  children: JSX.Element;
  userInfo?: IUserInfo | null;
};
export const useUserInfoContext = (): TProvidedValue => useContext(Context);
const USER_LOCAL_KEY = 'ishop_user_info';

export const UserInfoProvider = ({
  children,
  userInfo,
}: TUserInfoProviderProps): JSX.Element => {
  const [profile, setProfile] = useStorageState<Profile | null>(
    userInfo?.profile ? Profile.fromServer(userInfo?.profile) : null,
    USER_LOCAL_KEY,
    'local',
  );

  const [isLoading, setIsLoading] = useState(false);
  const ctx = useUserInfoContext();

  const invalidateProfile = useCallback(async () => {
    return await profileService
      .getProfile()
      .then((currentProfile: Profile | null = null) => {
        setProfile(currentProfile);
        return currentProfile;
      })
      .catch((error: unknown) => {
        console.error(error);
        return null;
      });
  }, []);

  const updateProfile = useCallback(
    async (updateProfileDTO: Partial<Profile>) => {
      setIsLoading(true);
      await profileService
        .updateProfile(updateProfileDTO)
        .then(invalidateProfile)
        .finally(() => setIsLoading(false));
    },
    [invalidateProfile],
  );

  const logout = useCallback(() => {
    authService.cleanToken();
    setProfile(null);
    return Promise.resolve();
  }, [setProfile]);

  useLayoutEffect(() => {
    isNull(profile) && setIsLoading(true);
    void invalidateProfile().finally(() => setIsLoading(false));
  }, [invalidateProfile]);

  const value = {
    ...ctx,
    profile,
    logout,
    pricePolicy:
      profile?.role === 'employee' ? 'employee' : ('regular' as TPricePolicy),
    isLoading,
    updateProfile,
    invalidateProfile,
  };

  return (
    <Context.Provider value={value}>
      <>
        {children}
        <AppLoader isShow={isLoading} />
      </>
    </Context.Provider>
  );
};
